import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '@/integrations/supabase/client';
import { Upload, X, Camera, AlertCircle, ZoomIn, ZoomOut, Move } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { validateFileContent, sanitizeFilename, getSecureContentType } from '@/utils/fileSecurityValidator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface AvatarUploadProps {
  onUpload: (url: string) => void;
  currentAvatar?: string;
  onRemove?: () => void;
  fallbackText?: string;
  maxSize?: number; // in MB
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  onUpload,
  currentAvatar,
  onRemove,
  fallbackText = 'AA',
  maxSize = 2
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorImage, setEditorImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const editorCanvasRef = useRef<HTMLCanvasElement>(null);
  const editorImageRef = useRef<HTMLImageElement>(null);

  const optimizeAndCropToCircle = async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = editorImageRef.current;
      const canvas = canvasRef.current || document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!img || !ctx) {
        reject(new Error('Failed to get canvas context or image'));
        return;
      }

      // Target size for avatar (512x512 for high quality, circular crop)
      const size = 512;
      canvas.width = size;
      canvas.height = size;

      // Fill with transparent background
      ctx.clearRect(0, 0, size, size);

      // Create circular clipping path
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Calculate dimensions with zoom and position
      const scaledWidth = img.naturalWidth * zoom;
      const scaledHeight = img.naturalHeight * zoom;
      
      // Center the image and apply position offset
      const drawX = (size - scaledWidth) / 2 + position.x;
      const drawY = (size - scaledHeight) / 2 + position.y;

      // Draw the image with zoom and position
      ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);
      ctx.restore();

      // Convert to optimized blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create optimized image'));
          }
        },
        'image/webp',
        0.85
      );
    });
  };

  const drawEditorPreview = () => {
    const canvas = editorCanvasRef.current;
    const img = editorImageRef.current;
    const ctx = canvas?.getContext('2d');
    
    if (!canvas || !img || !ctx) return;

    const size = 400;
    canvas.width = size;
    canvas.height = size;

    // Clear canvas
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, size, size);

    // Calculate dimensions with zoom
    const scaledWidth = img.naturalWidth * zoom;
    const scaledHeight = img.naturalHeight * zoom;
    
    // Center and apply position
    const drawX = (size - scaledWidth) / 2 + position.x;
    const drawY = (size - scaledHeight) / 2 + position.y;

    // Draw the image
    ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);

    // Draw circular overlay
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw border
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.stroke();
  };

  const handleFileSelected = async (file: File) => {
    try {
      setError(null);

      // Enhanced file validation
      const validation = await validateFileContent(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Validate file size
      if (file.size > maxSize * 1024 * 1024) {
        throw new Error(`File size must be less than ${maxSize}MB`);
      }

      // Validate file type
      const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!acceptedTypes.includes(file.type)) {
        throw new Error('Please upload a valid image file (JPEG, PNG, WebP, or GIF)');
      }

      // Load image into editor
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setEditorImage(dataUrl);
        setShowEditor(true);
        setZoom(1);
        setPosition({ x: 0, y: 0 });
      };
      reader.readAsDataURL(file);

    } catch (error: any) {
      setError(error.message);
      toast({
        title: "File validation failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleConfirmCrop = async () => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setShowEditor(false);

      setUploadProgress(30);

      // Optimize and crop to circular format with user adjustments
      const optimizedBlob = await optimizeAndCropToCircle();
      
      setUploadProgress(60);

      // Generate secure filename
      const fileName = `avatars/${Date.now()}-${Math.random().toString(36).substring(2)}.webp`;

      // Upload to Supabase storage
      const { data, error: uploadError } = await supabase.storage
        .from('images')
        .upload(fileName, optimizedBlob, {
          cacheControl: '31536000',
          upsert: false,
          contentType: 'image/webp'
        });

      setUploadProgress(90);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(fileName);

      setUploadProgress(100);
      onUpload(publicUrl);
      
      toast({
        title: "Success",
        description: "Avatar uploaded and optimized!"
      });

    } catch (error: any) {
      setError(error.message);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setEditorImage(null);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleFileSelected(acceptedFiles[0]);
    }
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Redraw preview when zoom or position changes
  React.useEffect(() => {
    if (showEditor && editorImage) {
      drawEditorPreview();
    }
  }, [zoom, position, showEditor, editorImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif']
    },
    multiple: false,
    maxSize: maxSize * 1024 * 1024
  });

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <div className="space-y-4">
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      {/* Image Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Position Your Avatar</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-center bg-muted rounded-lg p-4">
              <div className="relative">
                <canvas
                  ref={editorCanvasRef}
                  width={400}
                  height={400}
                  className="rounded-full cursor-move border-4 border-primary"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
                {editorImage && (
                  <img
                    ref={editorImageRef}
                    src={editorImage}
                    alt="Editor source"
                    style={{ display: 'none' }}
                    onLoad={drawEditorPreview}
                  />
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-muted-foreground" />
                <Slider
                  value={[zoom]}
                  onValueChange={(values) => setZoom(values[0])}
                  min={0.5}
                  max={3}
                  step={0.1}
                  className="flex-1"
                />
                <ZoomIn className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground w-12 text-right">
                  {Math.round(zoom * 100)}%
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Move className="w-4 h-4" />
                <span>Drag to reposition • Scroll to zoom</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCrop}>
              Apply & Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-6">
        {/* Avatar Preview */}
        <Avatar className="h-32 w-32 border-4 border-border">
          {currentAvatar ? (
            <AvatarImage src={currentAvatar} alt="Author avatar" />
          ) : (
            <AvatarFallback className="text-3xl font-semibold bg-muted">
              {fallbackText}
            </AvatarFallback>
          )}
        </Avatar>

        {/* Upload Controls */}
        <div className="flex-1 space-y-3">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/25'}
              ${uploading ? 'pointer-events-none opacity-50' : 'hover:border-primary hover:bg-primary/5'}
            `}
          >
            <input {...getInputProps()} />
            
            <div className="flex flex-col items-center space-y-2">
              {uploading ? (
                <div className="w-full space-y-2">
                  <Upload className="w-8 h-8 text-primary animate-pulse mx-auto" />
                  <Progress value={uploadProgress} className="w-full" />
                  <p className="text-sm text-muted-foreground">
                    Optimizing... {uploadProgress}%
                  </p>
                </div>
              ) : (
                <>
                  <Camera className="w-8 h-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {isDragActive ? 'Drop avatar here' : 'Upload author avatar'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Drag & drop or click to select
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Auto-crops to circular format • Max {maxSize}MB
                  </div>
                </>
              )}
            </div>
          </div>

          {currentAvatar && !uploading && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemove}
              className="w-full"
            >
              <X className="w-4 h-4 mr-2" />
              Remove Avatar
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
